import DesignTokens from '@tableau/design-tokens';
import {
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  HorizontalOriginOption,
  TabStyles,
  VerticalOriginOption
} from '@tableau/widgets';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { interpolateString } from '~src/commonInfra/utils/StringInterpolation';
import { applyColor }from '~src/style/ColorService';

import { ExplorerStyles } from '../../../components/Explorer/ExplorerStyles';
import { Hover } from '../../../components/Explorer/widgets/Hover';
export interface IInteractiveIconTooltip {
  text: string;
  title: string;
  iconClass?: string;
  tooltipText?: string;
  tooltipReplacements?: any;
  tooltipLink?: string;
  tooltipLinkText?: string;
}

export interface InteractiveIconTooltipState {
  isDialogOpen: boolean;
}

export function InteractiveIconTooltipCell(
  getProps: (row: DataGrid.Row) => IInteractiveIconTooltip): React.Factory<BodyCell.Props> {
  return React.createFactory(class extends React.Component<BodyCell.Props, InteractiveIconTooltipState> {
    private iconAnchorElement: HTMLElement;

    constructor(props: any) {
      super(props);
      this.state = {isDialogOpen: false};
    }

    private readonly hideDialog = () => {
      this.setState({isDialogOpen: false});
    };

    private readonly showDialog = () => {
      this.setState({isDialogOpen: true});
    };

    private readonly setAnchorElement = (el: HTMLElement) => {
      this.iconAnchorElement = el;
    };

    render() {
      const props = getProps(this.props.row);
      const link = {
        LINK: () =>
            <a
                data-tb-test-id={'cell-icon-hover-link'}
                className={'tb-data-grid-dialog-tooltip-link'}
                href={props.tooltipLink}
                style={{textDecoration: 'none', color: applyColor(DesignTokens.color.icon.default.discrete, TabStyles.Colors.Discrete), opacity: .80}}
            >
              {props.tooltipLinkText}
            </a>
      };
      return (
          <span data-tb-test-id={'plain-text-cell-icon'}>
          <Hover
              groupName="plainTextCellWithLinkInTooltipIcon"
              onEnter={this.showDialog}
              onLeave={this.hideDialog}
              leaveOnScroll={true}
              className={this.state.isDialogOpen ? props.iconClass + '-active' : props.iconClass}
              enterDelay={0}
              useDocument={false}
          >
            <div ref={this.setAnchorElement}>
              {this.state.isDialogOpen && <DialogWidget
                  testId={'interactive-tooltip'}
                  type={DialogType.Uber}
                  showCloseButton={false}
                  onCloseRequest={this.hideDialog}
                  positioningBehavior={DialogPositioningBehavior.Anchored}
                  anchorEl={this.iconAnchorElement}
                  anchorOrigin={{horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.bottom}}
                  stackedOrigin={{horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top}}
                  isModal={false}
                  showFooter={false}
                  dialogWidth={228}
                  dialogStyle={{marginTop: CommonStyles.Sizes.NormalIconSize}}
                  dialogContentStyle={{padding: CommonStyles.Sizes.MediumSpace, fontWeight: 'normal'}}
                  dialogBodyStyle={{maxWidth: '228px'}}
                  title={props.title}
              >
                  <div style={{color: applyColor(DesignTokens.color.font.default.default, CommonStyles.Colors.Black)}}>
                  {interpolateString(props.tooltipText, { ...props.tooltipReplacements, ...link })}
                  </div>
              </DialogWidget>}
            </div>
          </Hover>
          <span data-tb-test-id="data-grid-cell-text">
            {props.text}
          </span>
        </span>
      );
    }
  });
}

export interface IInteractiveImageIconTooltip {
  text: string;
  title: string;
  iconUrl: string;
  /** Optional reactive @tableau/icons node rendered instead of the iconUrl <img> when present. */
  iconNode?: React.ReactNode;
  iconClass?: string;
  iconTitle?: string;
  tooltipText?: string;
  tooltipLink?: string;
  tooltipLinkText?: string;
}

export function InteractiveImageIconTooltipCell(
    getProps: (row: DataGrid.Row) => IInteractiveImageIconTooltip
): React.Factory<BodyCell.Props> {
  return React.createFactory(class extends React.Component<BodyCell.Props, InteractiveIconTooltipState> {
    private iconAnchorElement: HTMLElement;

    constructor(props: any) {
      super(props);
      this.state = {isDialogOpen: false};
    }

    private readonly hideDialog = () => {
      this.setState({isDialogOpen: false});
    };

    private readonly showDialog = () => {
      this.setState({isDialogOpen: true});
    };

    private readonly setAnchorElement = (el: HTMLElement) => {
      this.iconAnchorElement = el;
    };

    render() {
      const props = getProps(this.props.row);
      /* eslint-disable jsx-a11y/alt-text */
      return (
          <Hover
              groupName="plainTextCellWithLinkInTooltipIcon"
              onEnter={this.showDialog}
              onLeave={this.hideDialog}
              leaveOnScroll={true}
              className={props.iconClass}
              enterDelay={0}
              useDocument={false}
          >
            <div ref={this.setAnchorElement}>
            <span data-tb-test-id={'plain-text-cell-icon'} title={props.iconTitle}>
              {props.iconNode ?? (
                <img data-tb-test-id="data-grid-cell-icon"
                     style={ExplorerStyles.statusIconStyle}
                     src={props.iconUrl}
                     title={props.iconTitle}/>
              )}
            </span>
              <span data-tb-test-id="data-grid-cell-text">
              {props.text}
            </span>
              {this.state.isDialogOpen && <DialogWidget
                  testId={'interactive-tooltip'}
                  type={DialogType.Uber}
                  showCloseButton={false}
                  onCloseRequest={this.hideDialog}
                  positioningBehavior={DialogPositioningBehavior.Anchored}
                  anchorEl={this.iconAnchorElement}
                  anchorOrigin={{horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.centerY}}
                  stackedOrigin={{horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.centerY}}
                  isModal={false}
                  showFooter={false}
                  dialogWidth={260}
                  dialogContentStyle={{padding: CommonStyles.Sizes.MediumSpace, fontWeight: 'normal'}}
                  title={props.title}
              >
                  <>
                    {props.tooltipText}
                      <a
                          target="_blank"
                          rel={'noopener noreferrer'}
                          data-tb-test-id={'cell-icon-hover-link'}
                          className={'tb-data-grid-dialog-tooltip-link'}
                          href={props.tooltipLink}
                          style={{textDecoration: 'none'}}
                      >
                        {props.tooltipLinkText}
                      </a>
                  </>
              </DialogWidget>}
            </div>
          </Hover>
      );
    }
  });
}
